import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Input } from "../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { cn } from "../lib/utils"
import { useBoundValue } from "../hooks/use-bound-value"

const COUNTRY_CODES = ["+1", "+44", "+82", "+81", "+49", "+33", "+86", "+91"]

function splitPhoneValue(value: string): { countryCode: string; phoneNumber: string } {
  const countryCode = [...COUNTRY_CODES]
    .sort((left, right) => right.length - left.length)
    .find((candidate) => value.startsWith(candidate)) ?? "+1"
  return {
    countryCode,
    phoneNumber: value.startsWith(countryCode) ? value.slice(countryCode.length) : value,
  }
}

export function PhoneInputRenderer({ element }: RendererProps) {
  const { dispatchEvent, itemContext, resolveSymbol } = useRenderContext()
  const [fullValue, setFullValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    typeof element.value === "string" ? element.value : "",
    String,
  )
  const { countryCode: code, phoneNumber: phone } = splitPhoneValue(fullValue)

  const dispatch = useCallback(
    (countryCode: string, phoneNumber: string) => {
      const value = `${countryCode}${phoneNumber}`
      setFullValue(value)
      // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
      dispatchEvent(element.id, "change", {
        ...(itemContext ?? {}),
        value,
        countryCode,
        phoneNumber,
      })
    },
    [element.id, dispatchEvent, itemContext, setFullValue],
  )

  return (
    <div
      className={cn("flex flex-row gap-2 items-center", element.className)}
      data-testid={`phone-input-${element.id}`}
      data-value={fullValue}
    >
      <Select
        value={code}
        onValueChange={(v) => {
          if (v === null) return
          dispatch(v, phone)
        }}
      >
        <SelectTrigger className="h-9 w-auto" aria-label="Country code">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COUNTRY_CODES.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        placeholder={element.placeholder ?? "(555) 123-4567"}
        value={phone}
        onChange={(e) => {
          dispatch(code, e.target.value)
        }}
        aria-label={element.ariaLabel ?? "Phone number"}
      />
    </div>
  )
}
